import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import YearSelector from './YearSelector';
import CashFlowChart from './CashFlowChart';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import api from '../utils/api';
import { Building, Plus, TrendingUp, TrendingDown, DollarSign, AlertTriangle, CheckCircle, Edit, Trash2 } from 'lucide-react';

const BankBalances = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cashFlow, setCashFlow] = useState({ months: [] });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [showEditAccount, setShowEditAccount] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [showCashReserveModal, setShowCashReserveModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [cashReserves, setCashReserves] = useState({});
  
  const [newAccount, setNewAccount] = useState({
    company_id: '',
    bank_name: '',
    account_name: '',
    account_number: '',
    iban: ''
  });

  useEffect(() => {
    fetchCompanies();
    fetchBankAccounts();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchCashFlow();
      fetchCashReserves();
      fetchChartData();
    }
  }, [selectedCompany, currentYear]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
      if (response.data.length > 0 && !selectedCompany) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await api.get('/bank-accounts');
      setBankAccounts(response.data);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    if (!selectedCompany) return;
    
    try {
      const response = await api.get(`/cash-flow-chart/${selectedCompany}?year=${currentYear}`);
      setChartData(response.data.chart_data || []);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const fetchCashFlow = async () => {
    if (!selectedCompany) return;
    
    try {
      const response = await api.get(`/cash-flow/${selectedCompany}?year=${currentYear}`);
      setCashFlow(response.data);
    } catch (error) {
      console.error('Error fetching cash flow:', error);
    }
  };

  const fetchCashReserves = async () => {
    if (!selectedCompany) return;
    
    try {
      const response = await api.get(`/monthly-cash-reserves?year=${currentYear}`);
      const reservesMap = {};
      response.data.forEach(reserve => {
        const key = `${reserve.bank_account_id}_${reserve.month}`;
        reservesMap[key] = reserve.cash_reserve;
      });
      setCashReserves(reservesMap);
    } catch (error) {
      console.error('Error fetching cash reserves:', error);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      await api.post('/bank-accounts', { ...newAccount, company_id: selectedCompany });
      setShowNewAccount(false);
      setNewAccount({
        company_id: '',
        bank_name: '',
        account_name: '',
        account_number: '',
        iban: ''
      });
      fetchBankAccounts();
      alert('Conto bancario creato con successo!');
    } catch (error) {
      console.error('Error creating bank account:', error);
      alert('Errore nella creazione del conto bancario');
    }
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/bank-accounts/${editingAccount.id}`, {
        company_id: editingAccount.company_id,
        bank_name: editingAccount.bank_name,
        account_name: editingAccount.account_name,
        account_number: editingAccount.account_number,
        iban: editingAccount.iban
      });
      setShowEditAccount(false);
      setEditingAccount(null);
      fetchBankAccounts();
      alert('Conto bancario aggiornato con successo!');
    } catch (error) {
      console.error('Error updating bank account:', error);
      alert('Errore nell\'aggiornamento del conto bancario');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!confirm('Sei sicuro di voler eliminare questo conto bancario? Verranno eliminate anche tutte le riserve di cassa associate.')) {
      return;
    }
    
    try {
      await api.delete(`/bank-accounts/${accountId}`);
      fetchBankAccounts();
      alert('Conto bancario eliminato con successo!');
    } catch (error) {
      console.error('Error deleting bank account:', error);
      alert('Errore nell\'eliminazione del conto bancario');
    }
  };

  const handleSaveCashReserve = async (month, value) => {
    const companyAccounts = bankAccounts.filter(acc => acc.company_id === selectedCompany);
    
    if (companyAccounts.length === 0) {
      alert('Crea prima un conto bancario per questa azienda');
      return;
    }
    
    // For simplicity, save to the first account. In a real app, you'd select which account
    const accountId = companyAccounts[0].id;
    
    try {
      await api.post('/monthly-cash-reserves', {
        bank_account_id: accountId,
        year: currentYear,
        month: month,
        cash_reserve: parseFloat(value)
      });
      
      fetchCashReserves();
      fetchCashFlow();
      alert('Riserva di cassa salvata con successo!');
    } catch (error) {
      console.error('Error saving cash reserve:', error);
      alert('Errore nel salvataggio della riserva di cassa');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'green': { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle, label: 'Positivo' },
      'orange': { color: 'bg-orange-100 text-orange-800 border-orange-300', icon: AlertTriangle, label: 'Attenzione' },
      'red': { color: 'bg-red-100 text-red-800 border-red-300', icon: AlertTriangle, label: 'Critico' }
    };
    
    const badge = badges[status] || badges['green'];
    const Icon = badge.icon;
    
    return (
      <Badge className={`${badge.color} border px-3 py-1 flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </Badge>
    );
  };

  const companyAccounts = bankAccounts.filter(acc => acc.company_id === selectedCompany);

  if (loading) {
    return (
      <Layout title="Saldi Banche">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Saldi Banche - Cash Flow Management">
      <div className="space-y-6">
        {/* Header */}
        <Card className="p-6 border-0 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">💰 Saldi Banche</h2>
              <p className="text-sm text-slate-600">Gestisci i conti bancari e monitora il cash flow aziendale</p>
            </div>
            
            <Dialog open={showNewAccount} onOpenChange={setShowNewAccount}>
              <DialogTrigger asChild>
                <Button className="bg-blue-900 hover:bg-blue-800">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo Conto Bancario
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nuovo Conto Bancario</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateAccount} className="space-y-4">
                  <div>
                    <Label>Banca *</Label>
                    <Input
                      value={newAccount.bank_name}
                      onChange={(e) => setNewAccount({ ...newAccount, bank_name: e.target.value })}
                      placeholder="es. Intesa Sanpaolo"
                      required
                    />
                  </div>
                  <div>
                    <Label>Nome Conto *</Label>
                    <Input
                      value={newAccount.account_name}
                      onChange={(e) => setNewAccount({ ...newAccount, account_name: e.target.value })}
                      placeholder="es. Conto Corrente Principale"
                      required
                    />
                  </div>
                  <div>
                    <Label>Numero Conto</Label>
                    <Input
                      value={newAccount.account_number}
                      onChange={(e) => setNewAccount({ ...newAccount, account_number: e.target.value })}
                      placeholder="Opzionale"
                    />
                  </div>
                  <div>
                    <Label>IBAN</Label>
                    <Input
                      value={newAccount.iban}
                      onChange={(e) => setNewAccount({ ...newAccount, iban: e.target.value })}
                      placeholder="IT00X0000000000000000000000"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowNewAccount(false)}>
                      Annulla
                    </Button>
                    <Button type="submit" className="bg-blue-900 hover:bg-blue-800">
                      Crea Conto
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </Card>

        {/* Company Selector & Year Selector */}
        <Card className="p-4 border-0 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Seleziona Azienda</Label>
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona un'azienda" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Anno</Label>
              <YearSelector currentYear={currentYear} onChange={setCurrentYear} />
            </div>
          </div>
        </Card>

        {/* Bank Accounts List */}
        <Card className="p-6 border-0 shadow-lg">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Conti Bancari</h3>
          {companyAccounts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Building className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>Nessun conto bancario per questa azienda</p>
              <p className="text-sm">Crea il primo conto per iniziare</p>
            </div>
          ) : (
            <div className="space-y-2">
              {companyAccounts.map(account => (
                <div key={account.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <div className="font-semibold text-slate-900">{account.bank_name}</div>
                    <div className="text-sm text-slate-600">{account.account_name}</div>
                    {account.iban && (
                      <div className="text-xs text-slate-500 mt-1">IBAN: {account.iban}</div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingAccount(account);
                        setShowEditAccount(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteAccount(account.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>



        {/* Cash Flow Chart */}
        {selectedCompany && chartData.length > 0 && (
          <CashFlowChart data={chartData} year={currentYear} />
        )}

        {/* Cash Flow Table */}
        {selectedCompany && cashFlow.months && cashFlow.months.length > 0 && (
          <Card className="p-6 border-0 shadow-lg">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              Cash Flow {currentYear}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>
                    <th className="p-3 text-left font-semibold text-slate-800">Mese</th>
                    <th className="p-3 text-right font-semibold text-slate-800">Riserva Iniziale</th>
                    <th className="p-3 text-right font-semibold text-slate-800">Entrate</th>
                    <th className="p-3 text-right font-semibold text-slate-800">Uscite</th>
                    <th className="p-3 text-right font-semibold text-slate-800">Risultato</th>
                    <th className="p-3 text-right font-semibold text-slate-800">Saldo Finale</th>
                    <th className="p-3 text-center font-semibold text-slate-800">Stato</th>
                    <th className="p-3 text-center font-semibold text-slate-800">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {cashFlow.months.map(month => (
                    <tr key={month.month} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3">
                        <div className="font-medium text-slate-900">{month.month_name}</div>
                        {month.is_future && (
                          <div className="text-xs text-blue-600">Previsione</div>
                        )}
                        {month.is_current && (
                          <div className="text-xs text-green-600">Mese corrente</div>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <div className="font-semibold text-slate-900">
                          €{month.riserva_iniziale.toFixed(2)}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="text-green-600 font-medium flex items-center justify-end gap-1">
                          <TrendingUp className="h-4 w-4" />
                          €{month.entrate.toFixed(2)}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="text-red-600 font-medium flex items-center justify-end gap-1">
                          <TrendingDown className="h-4 w-4" />
                          €{month.uscite.toFixed(2)}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className={`font-semibold ${month.risultato >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          €{month.risultato.toFixed(2)}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className={`font-bold text-lg ${month.saldo_finale >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          €{month.saldo_finale.toFixed(2)}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        {getStatusBadge(month.status)}
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const currentReserve = month.riserva_iniziale;
                            const newReserve = prompt(`Inserisci riserva di cassa per ${month.month_name}:`, currentReserve);
                            if (newReserve !== null) {
                              handleSaveCashReserve(month.month, newReserve);
                            }
                          }}
                        >
                          <DollarSign className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Legend */}
        <Card className="p-4 border-0 shadow-lg bg-blue-50">
          <h4 className="font-semibold text-blue-900 mb-2">📊 Legenda Stati:</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className="text-slate-700"><strong>Verde:</strong> Entrate ≥ Uscite</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-500 rounded"></div>
              <span className="text-slate-700"><strong>Arancione:</strong> Uscite coperte da riserva</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded"></div>
              <span className="text-slate-700"><strong>Rosso:</strong> Uscite superano liquidità disponibile</span>
            </div>
          </div>
        </Card>

        {/* Edit Account Modal */}
        <Dialog open={showEditAccount} onOpenChange={setShowEditAccount}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifica Conto Bancario</DialogTitle>
            </DialogHeader>
            {editingAccount && (
              <form onSubmit={handleUpdateAccount} className="space-y-4">
                <div>
                  <Label>Banca *</Label>
                  <Input
                    value={editingAccount.bank_name}
                    onChange={(e) => setEditingAccount({ ...editingAccount, bank_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Nome Conto *</Label>
                  <Input
                    value={editingAccount.account_name}
                    onChange={(e) => setEditingAccount({ ...editingAccount, account_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Numero Conto</Label>
                  <Input
                    value={editingAccount.account_number || ''}
                    onChange={(e) => setEditingAccount({ ...editingAccount, account_number: e.target.value })}
                  />
                </div>
                <div>
                  <Label>IBAN</Label>
                  <Input
                    value={editingAccount.iban || ''}
                    onChange={(e) => setEditingAccount({ ...editingAccount, iban: e.target.value })}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEditAccount(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-blue-900 hover:bg-blue-800">
                    Salva Modifiche
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default BankBalances;
